// frontend/src/features/seo/components/SchemaStudioModal.js
import React, { useState, useEffect } from 'react';
import SchemaMarkupTab from './SchemaMarkupTab';

/**
 * Modal shell for the Schema.org Studio. Renders the existing SchemaMarkupTab
 * inside a dialog. Entered from a Content Brief ("Add JSON Structure Data") or
 * from the Content Studio publish flow (when an article has no JSON-LD yet).
 *
 * Close guard: if the user populated/edited schema but did not click
 * "Auto Update" (which injects it into the brief), closing shows a confirm so
 * the generated schema is not silently lost.
 */
export default function SchemaStudioModal({
  isOpen,
  onClose,
  schemaTemplates,
  briefs = [],
  pageAudit = null,
  topicClusters = [],
  onUpdateBriefContent = null,
  onOpenInStudio = null,
  initialBriefId = '',
  autoSeedMode = null,
  onAutoUpdated = null,
}) {
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Reset guard state whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setIsDirty(false);
      setShowCloseConfirm(false);
    }
  }, [isOpen, initialBriefId]);

  if (!isOpen) return null;

  const requestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
      return;
    }
    onClose();
  };

  // Auto Update applied the schema: clear the guard and hand back to the parent.
  const handleAutoUpdated = (updatedBrief) => {
    setIsDirty(false);
    if (onAutoUpdated) onAutoUpdated(updatedBrief);
    onClose();
  };

  return (
    <div className="seo-modal-overlay" onClick={requestClose} data-testid="schema-studio-modal">
      <div
        className="seo-modal-dialog seo-schema-studio-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schema-studio-title"
      >
        <div className="seo-modal-header">
          <h3 id="schema-studio-title" className="seo-modal-title">
            <i className="fas fa-code text-purple"></i> Structured Data &amp; Schema.org Studio
          </h3>
          <button
            type="button"
            className="seo-modal-close-btn"
            onClick={requestClose}
            title="Close Schema Studio"
            aria-label="Close"
            data-testid="schema-studio-close-btn"
          >
            &times;
          </button>
        </div>

        <div className="seo-schema-studio-modal-body">
          <SchemaMarkupTab
            schemaTemplates={schemaTemplates}
            briefs={briefs}
            pageAudit={pageAudit}
            topicClusters={topicClusters}
            onUpdateBriefContent={onUpdateBriefContent}
            onOpenInStudio={onOpenInStudio}
            initialBriefId={initialBriefId}
            autoSeedMode={autoSeedMode}
            onAutoUpdated={handleAutoUpdated}
            onDirtyChange={setIsDirty}
          />
        </div>

        {showCloseConfirm && (
          <div className="seo-publish-confirm-overlay" data-testid="schema-close-confirm-dialog">
            <div className="seo-publish-confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="seo-publish-confirm-header">
                <h4>
                  <i className="fas fa-triangle-exclamation text-amber"></i> Discard generated
                  schema?
                </h4>
              </div>
              <div className="seo-publish-confirm-body">
                <p className="seo-confirm-subtitle">
                  You generated structured data but haven't clicked <strong>Auto Update</strong> to
                  add it to the article. If you close now, the schema will not be applied.
                </p>
              </div>
              <div className="seo-publish-confirm-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => setShowCloseConfirm(false)}
                  data-testid="schema-close-confirm-stay-btn"
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary"
                  onClick={() => {
                    setShowCloseConfirm(false);
                    onClose();
                  }}
                  data-testid="schema-close-confirm-discard-btn"
                >
                  Close without applying
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
